/**
 * Раскладка листа развёрток.
 *
 * Стены помещения ставятся в ряд, слева направо, и стоят на общем уровне пола —
 * как лента развёртки на настоящем листе. Ряд, а не столбик, потому что рабочая
 * область широкая: так все стены помещения видно сразу и без мелкого масштаба.
 *
 * Вокруг каждой стены оставлены поля под размерные цепочки: снизу — положения
 * проёмов и точек, слева — высота стены, справа — отметки установки.
 *
 * Мир измеряется в миллиметрах, ось Y смотрит ВНИЗ (как в SVG), поэтому
 * высота на стене переводится в мир вычитанием из отметки пола.
 */
import type { WallElevation } from "@/widgets/plan";

/** Толщина перекрытия, которое рисуется сверху и снизу развёртки, мм. */
export const SLAB = 200;

const MARGIN_LEFT = 600;
// Справа помещается несколько отметок установки, поставленных одна за другой.
// Поля держим компактными, чтобы листы не разъезжались по холсту.
const MARGIN_RIGHT = 800;
const MARGIN_TOP = 700;
const MARGIN_BOTTOM = 1200;

/** Одна стена на листе: где её левый нижний угол в мировых координатах. */
export interface SheetLayout {
	elevation: WallElevation;
	/** Левый край стены */
	x: number;
	/** Отметка пола (мировая Y) */
	floorY: number;
}

export interface ElevationsLayout {
	sheets: SheetLayout[];
	bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

export const layoutElevations = (elevations: WallElevation[]): ElevationsLayout => {
	const sheets: SheetLayout[] = [];
	if (elevations.length === 0) {
		return { sheets, bounds: { minX: 0, minY: 0, maxX: 1000, maxY: 1000 } };
	}

	// Общий уровень пола для всех стен — по самой высокой
	const tallest = Math.max(...elevations.map((e) => e.height));
	const floorY = MARGIN_TOP + SLAB + tallest;

	let cursor = 0;
	for (const elevation of elevations) {
		const x = cursor;
		sheets.push({ elevation, x, floorY });
		cursor = x + elevation.width;
	}

	return {
		sheets,
		bounds: {
			minX: -MARGIN_LEFT,
			minY: 0,
			maxX: cursor + MARGIN_RIGHT,
			maxY: floorY + SLAB + MARGIN_BOTTOM,
		},
	};
};

/** Мировая точка по координатам развёртки: x вдоль стены, y — высота от пола. */
export const sheetPoint = (sheet: SheetLayout, x: number, y: number) => ({
	x: sheet.x + x,
	y: sheet.floorY - y,
});
